import { signRequest } from "@shared/crypto/signRequest";

import type { FileAttachment } from "shared";

// Мост страница ↔ Service Worker для стриминга видео.
//
// SW расшифровывает чанки на лету и отдаёт <video> по виртуальному URL
// /media-stream/... с поддержкой Range (память константная, перемотка без
// полного скачивания). Но подписывать запросы к серверу SW не может — приватный
// ключ живёт в sessionStorage окна. Поэтому:
//   1) страница регистрирует стрим (передаёт AES-ключ и параметры чанков);
//   2) SW на каждый диапазон просит страницу подписать GET (postMessage
//      round-trip) — подписи всегда свежие, окно ±5 мин не проблема;
//   3) реестр SW живёт в его памяти, а браузер убивает простаивающий SW —
//      на неизвестный путь SW спрашивает метаданные у страницы (mc-meta) и
//      восстанавливает стрим из локального зеркала реестра.

interface StreamMetaPayload {
    url: string;
    key: string;
    baseNonce: string;
    chunkSize: number;
    size: number;
    mimeType: string;
}

interface SignMessage {
    type: "mc-sign";
    reqId: number;
    method: string;
    url: string;
}

interface MetaMessage {
    type: "mc-meta";
    reqId: number;
    path: string;
}

// Зеркало реестра на странице: SW восстанавливает из него стримы после
// собственного рестарта.
const registered = new Map<string, StreamMetaPayload>();

let responderInstalled = false;

// Отвечает SW на запросы подписи и метаданных. Один слушатель на страницу.
function installResponder(): void {
    if (responderInstalled) {
        return;
    }
    responderInstalled = true;
    navigator.serviceWorker.addEventListener("message", (event) => {
        const data = event.data as SignMessage | MetaMessage | undefined;
        if (data?.type === "mc-sign") {
            void (async () => {
                let headers: Record<string, string> | null = null;
                try {
                    headers = await signRequest(data.method, data.url);
                } catch {
                    headers = null;
                }
                navigator.serviceWorker.controller?.postMessage({
                    type: "mc-sign-result",
                    reqId: data.reqId,
                    headers,
                });
            })();
        } else if (data?.type === "mc-meta") {
            navigator.serviceWorker.controller?.postMessage({
                type: "mc-meta-result",
                reqId: data.reqId,
                meta: registered.get(data.path) ?? null,
            });
        }
    });
}

// Регистрирует вложение в SW и возвращает виртуальный URL для <video>.
// null — стриминг недоступен (нет SW-контроля / легаси-формат), зови блоб-путь.
export async function registerStream(
    att: FileAttachment
): Promise<string | null> {
    if (!("serviceWorker" in navigator)) {
        return null;
    }
    const controller = navigator.serviceWorker.controller;
    if (!controller || !att.baseNonce || !att.chunkSize) {
        return null;
    }
    const m = /^\/api\/files\/([^/]+)\/([^/]+)$/.exec(att.url);
    if (!m) {
        return null;
    }
    installResponder();

    const path = `/media-stream/${m[1]}/${m[2]}`;
    const meta: StreamMetaPayload = {
        url: att.url,
        key: att.key,
        baseNonce: att.baseNonce,
        chunkSize: att.chunkSize,
        size: att.size,
        mimeType: att.mimeType,
    };
    registered.set(path, meta);

    const ok = await new Promise<boolean>((resolve) => {
        const channel = new MessageChannel();
        const timer = setTimeout(() => resolve(false), 3000);
        channel.port1.onmessage = () => {
            clearTimeout(timer);
            resolve(true);
        };
        controller.postMessage({ type: "mc-stream-register", path, meta }, [
            channel.port2,
        ]);
    });
    return ok ? path : null;
}
